/**
 * Reports/index.js — Master Controller
 * Owns all state, data loading, and section orchestration.
 * Delegates rendering to section components, exports to exportUtils.
 *
 * Structure:
 *   config/     — Static data (MITRE, ports, severity, breach costs)
 *   utils/      — Calculation functions
 *   sections/   — Report section components
 *   preflight/  — Dataset quality analysis
 */

import React, { useState, useMemo, useCallback, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import axios from "axios";
import PageLayout from "../../components/PageLayout";
import { useSharedDataset } from "../../DataContext";
import { theme } from "../../theme";

// Config
import { API, TLP_OPTIONS, SEVERITY_COLORS, CHART_COLORS, PRINT_CSS } from "./config/constants";
import { INDUSTRY_COSTS }                                                from "./config/breach";
import { severityFromLabel }                                             from "./config/severity";

// Utils
import { formatDate, parseTimestamp }              from "./utils/formatUtils";
import { calculateRiskScore, getRiskLabel,
         estimateMTTD, calculateAlertFatigue,
         generateFirewallRules, getRiskScoreBreakdown } from "./utils/riskUtils";
import { estimateBreachCost }                      from "./utils/breachUtils";

// Sections
import ReportHeader          from "./sections/ReportHeader";
import ReportSummary         from "./sections/ReportSummary";
import ReportRisk            from "./sections/ReportRisk";
import ReportTimeline        from "./sections/ReportTimeline";
import ReportAttackAnalysis  from "./sections/ReportAttackAnalysis";
import ReportNetworkAnalysis from "./sections/ReportNetworkAnalysis";
import ReportHeatmap         from "./sections/ReportHeatmap";
import ReportMLMetrics       from "./sections/ReportMLMetrics";
import ReportThreatIntel     from "./sections/ReportThreatIntel";
import ReportCompliance      from "./sections/ReportCompliance";
import ReportBreachCost      from "./sections/ReportBreachCost";
import ReportRecommendations from "./sections/ReportRecommendations";
import ReportFindings        from "./sections/ReportFindings";

// Pre-flight
import DatasetPreFlight from "./preflight/DatasetPreFlight";

// ── Section Groups & Registry ─────────────────────────────────────────────────

export const SECTION_GROUPS = {
  executive:       { label: "Executive",       color: theme.colors.accent, sections: ["summary","risk"]                          },
  technical:       { label: "Technical",       color: theme.colors.accent, sections: ["timeline","attack","network","heatmap"]   },
  ml:              { label: "ML Intelligence", color: "#00695C", sections: ["ml"]                                      },
  threat:          { label: "Threat Intel",    color: "#C62828", sections: ["threat"]                                  },
  compliance:      { label: "Compliance",      color: "#E65100", sections: ["compliance"]                              },
  recommendations: { label: "Recommendations", color: "#2E7D32", sections: ["recommendations","breachcost"]            },
  forensic:        { label: "Forensic",        color: "#4E342E", sections: ["findings"]                                },
};

export const ALL_SECTIONS = [
  { id:"summary",         label:"Executive Summary",      group:"executive"       },
  { id:"risk",            label:"Risk Assessment",        group:"executive"       },
  { id:"timeline",        label:"Attack Timeline",        group:"technical"       },
  { id:"attack",          label:"Attack Analysis",        group:"technical"       },
  { id:"network",         label:"Network Analysis",       group:"technical"       },
  { id:"heatmap",         label:"Anomaly Heatmap",        group:"technical"       },
  { id:"ml",              label:"ML Model Metrics",       group:"ml"              },
  { id:"threat",          label:"Threat Intelligence",    group:"threat"          },
  { id:"compliance",      label:"Compliance Mapping",     group:"compliance"      },
  { id:"recommendations", label:"Recommendations",        group:"recommendations" },
  { id:"breachcost",      label:"Breach Cost Analysis",   group:"recommendations" },
  { id:"findings",        label:"Forensic Findings",      group:"forensic"        },
];

// ── Main Component ────────────────────────────────────────────────────────────

export default function Reports() {
  const { dataset, setDataset, pageResults } = useSharedDataset();
  const data     = dataset.data     || [];
  const columns  = dataset.columns  || [];
  const fileName = dataset.fileName || "";
  const mlResult = pageResults?.["dashboard"] || null;

  // Report config
  const [reportTitle, setReportTitle] = useState("Cyber Security Incident Report");
  const [analyst,     setAnalyst]     = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("nexgen_user") || "{}").username || "Analyst"; }
    catch { return "Analyst"; }
  });
  const [tlp,      setTlp]      = useState("AMBER");
  const [industry, setIndustry] = useState("global");
  const [loading,  setLoading]  = useState(false);

  // Section toggles — all on by default
  const [enabledSections, setEnabledSections] = useState(
    ALL_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );

  // AI narrative
  const [aiNarrative, setAiNarrative] = useState("");
  const [aiImpact,    setAiImpact]    = useState("");
  const [aiLoading,   setAiLoading]   = useState(false);
  const [aiError,     setAiError]     = useState("");

  const printRef = useRef();

  // ── File Upload ─────────────────────────────────────────────────────────────

  const handleFile = useCallback((e) => {
    const f = e.target.files[0];
    if (!f) return;
    setLoading(true);
    if (f.name.toLowerCase().endsWith(".csv")) {
      Papa.parse(f, {
        header: true, skipEmptyLines: true,
        complete: (res) => {
          setDataset({ file: f, fileName: f.name, data: res.data, columns: Object.keys(res.data[0] || {}) });
          setLoading(false);
        }
      });
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const wb    = XLSX.read(evt.target.result);
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows  = XLSX.utils.sheet_to_json(sheet);
        setDataset({ file: f, fileName: f.name, data: rows, columns: Object.keys(rows[0] || {}) });
        setLoading(false);
      };
      reader.readAsArrayBuffer(f);
    }
  }, [setDataset]);

  // ── Computed Stats ──────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (!data.length) return null;
    const colsNorm = columns.map(c => c.toLowerCase().trim().replace(/[\s_\-\.]+/g,""));
    const findCol  = (candidates) => {
      for (const c of candidates) {
        const norm = c.toLowerCase().replace(/[\s_\-\.]+/g,"");
        const idx  = colsNorm.indexOf(norm);
        if (idx !== -1) return columns[idx];
      }
      return null;
    };
    const attackCol  = findCol(["label","type","attack_type","attacktype","class","category","attack","signature","Label","Attack Type","attackcat"]);
    const srcIPCol   = findCol(["src_ip","srcip","sourceip","source_ip","src","id.orig_h","Source IP","Src IP"]);
    const dstIPCol   = findCol(["dst_ip","dstip","destip","dest_ip","dst","id.resp_h","Destination IP","Dst IP"]);
    const srcPortCol = findCol(["src_port","sport","sourceport","id.orig_p","Source Port"]);
    const dstPortCol = findCol(["dst_port","dport","destport","id.resp_p","Destination Port","destinationport"]);
    const protoCol   = findCol(["proto","protocol","transport","Protocol"]);
    const timeCol    = findCol(["time","timestamp","date","datetime","ts","Timestamp"]);
    const bytesCol   = findCol(["bytes","length","size","sbytes","Total Length","Payload"]);

    const sevCounts  = { critical:0, high:0, medium:0, low:0, info:0 };
    const attackDist = {}, srcIPDist = {}, dstIPDist = {}, portDist = {}, protoDist = {}, attackPortDist = {};
    const hourDist   = Array(24).fill(0);

    data.forEach(row => {
      const label = attackCol ? String(row[attackCol] || "").trim() : "unknown";
      const sev   = severityFromLabel(label);
      sevCounts[sev] = (sevCounts[sev] || 0) + 1;

      if (attackCol)  attackDist[label]                                                 = (attackDist[label]  || 0) + 1;
      if (srcIPCol)   { const v=String(row[srcIPCol]||"").trim();   if(v&&v!=="0.0.0.0") srcIPDist[v]  = (srcIPDist[v]  || 0) + 1; }
      if (dstIPCol)   { const v=String(row[dstIPCol]||"").trim();   if(v&&v!=="0.0.0.0") dstIPDist[v]  = (dstIPDist[v]  || 0) + 1; }
      if (dstPortCol) { const v=String(row[dstPortCol]||"").trim(); if(v&&v!=="0")       portDist[v]   = (portDist[v]   || 0) + 1; }
      if (protoCol)   { const v=String(row[protoCol]||"").trim().toUpperCase(); if(v)    protoDist[v]  = (protoDist[v]  || 0) + 1; }
      if (timeCol)    { try { const d=parseTimestamp(row[timeCol]); if(d) hourDist[d.getHours()]++; } catch{} }

      // Attack-to-port correlation
      if (attackCol && dstPortCol && sev !== "info") {
        const port = String(row[dstPortCol] || "").trim();
        if (port && port !== "0") attackPortDist[port] = (attackPortDist[port] || 0) + 1;
      }
    });

    const toArr = (obj) => Object.entries(obj).map(([name,value]) => ({name,value})).sort((a,b) => b.value-a.value);
    const validHourCount = hourDist.reduce((s,v) => s+v, 0);

    return {
      total:      data.length,
      critical:   sevCounts.critical,
      high:       sevCounts.high,
      medium:     sevCounts.medium,
      low:        sevCounts.low,
      info:       sevCounts.info,
      attackDist: toArr(attackDist),
      srcIPDist:  toArr(srcIPDist).slice(0,20),
      dstIPDist:  toArr(dstIPDist).slice(0,20),
      portDist:   toArr(portDist).slice(0,20),
      attackPortDist: toArr(attackPortDist).slice(0,10),
      protoDist:  toArr(protoDist),
      hourDist:   validHourCount > data.length * 0.01 ? hourDist : Array(24).fill(0),
      hasValidTime: validHourCount > data.length * 0.01,
      attackCol, srcIPCol, dstIPCol, srcPortCol, dstPortCol, protoCol, timeCol, bytesCol,
    };
  }, [data, columns]);

  // ── Derived metrics ─────────────────────────────────────────────────────────

  const riskScore     = useMemo(() => calculateRiskScore(stats, mlResult), [stats, mlResult]);
  const riskLabel     = useMemo(() => getRiskLabel(riskScore), [riskScore]);
  const breachCost    = useMemo(() => stats ? estimateBreachCost({ critical:stats.critical, high:stats.high, medium:stats.medium, low:stats.low }, industry) : 0, [stats, industry]);
  const mttd          = useMemo(() => mlResult ? estimateMTTD(stats?.total, mlResult.anomaly_rate) : "N/A", [stats, mlResult]);
  const alertFatigue  = useMemo(() => stats ? calculateAlertFatigue(stats.total, stats.critical, stats.high) : 0, [stats]);
  const firewallRules = useMemo(() => stats ? generateFirewallRules(stats.srcIPDist, stats.portDist, stats.attackDist) : [], [stats]);

  // ── AI Narrative ────────────────────────────────────────────────────────────

  const generateNarrative = useCallback(async () => {
    if (!stats) return;
    setAiLoading(true); setAiError(""); setAiNarrative(""); setAiImpact("");
    const prompt = `You are a senior cybersecurity analyst. Write a formal incident report in two sections separated by [IMPACT].
Dataset: ${fileName} | Events: ${stats.total.toLocaleString()} | Critical: ${stats.critical} | High: ${stats.high}
Top attacks: ${stats.attackDist.slice(0,5).map(a=>`${a.name}(${a.value})`).join(", ")}
Risk score: ${riskScore}/100 (${riskLabel.label})
Section 1 - Executive Summary (~200 words, 3 paragraphs, no bullets, professional tone)
[IMPACT]
Section 2 - Business Impact (~100 words, 2 paragraphs)`;
    try {
      const res = await axios.post(`${API}/report/narrative`, { prompt });
      const text = res.data?.narrative || "";
      const parts = text.split("[IMPACT]");
      setAiNarrative(parts[0]?.trim() || text.trim());
      setAiImpact(parts[1]?.trim() || "");
    } catch (err) { setAiError(`AI generation failed: ${err.message}`); }
    finally { setAiLoading(false); }
  }, [stats, fileName, riskScore, riskLabel, mlResult]);

  // ── Section Toggles ─────────────────────────────────────────────────────────

  const toggleSection = (id) => setEnabledSections(p => ({ ...p, [id]: !p[id] }));
  const toggleGroup   = (gid) => {
    const sects = SECTION_GROUPS[gid].sections;
    const allOn = sects.every(s => enabledSections[s]);
    setEnabledSections(p => { const n={...p}; sects.forEach(s=>{n[s]=!allOn;}); return n; });
  };
  const enableAll  = () => setEnabledSections(ALL_SECTIONS.reduce((a,s)=>({...a,[s.id]:true}),{}));
  const disableAll = () => setEnabledSections(ALL_SECTIONS.reduce((a,s)=>({...a,[s.id]:false}),{}));

  // ── HTML document builder for Export + Print ────────────────────────────────

  const buildReportHTML = useCallback((autoPrint = false) => {
    const el = document.getElementById("nexgen-report");
    if (!el) return "";
    const reportClone = el.cloneNode(true);
    const preflight = reportClone.querySelector("[data-export-include='preflight']");
    if (preflight) {
      preflight.classList.remove("no-print");
      preflight.classList.add("export-preflight-page");
    }
    reportClone.querySelectorAll(".no-print").forEach(node => {
      if (!node.closest("[data-export-include='preflight']")) node.remove();
    });
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>${reportTitle}</title>
  <style>
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, sans-serif; background: #fff; color: #1A1A2E; margin: 0; padding: 20px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
    th { background: #F5F5F5; padding: 8px 10px; text-align: left; border-bottom: 2px solid #E0E0E0; font-size: 11px; }
    td { padding: 7px 10px; border-bottom: 1px solid #E0E0E0; font-size: 11px; }
    .report-card { background: #fff; border: 1px solid #E0E0E0; border-radius: 8px; padding: 20px; margin-bottom: 16px; }
    .report-export-footer {
      margin-top: 28px;
      padding-top: 10px;
      border-top: 1px solid #D8C2A2;
      text-align: center;
      font-family: monospace;
      font-size: 9px;
      line-height: 1.45;
      color: #755C57;
    }
    .no-print { display: none !important; }
    h1,h2,h3 { color: #1A1A2E; }
    .export-preflight-page {
      break-after: page;
      page-break-after: always;
      break-inside: avoid;
      page-break-inside: avoid;
      margin-bottom: 0;
    }
    .report-section,
    .report-card,
    [style*="border-radius"],
    svg {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    tr, thead, tbody {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    img, svg, canvas {
      max-width: 100%;
    }
    @media print {
      body { padding: 0; }
      .export-preflight-page {
        min-height: calc(100vh - 24mm);
        overflow: visible !important;
      }
      .report-section,
      .report-card {
        overflow: visible !important;
      }
      .report-export-footer {
        font-size: 8px !important;
        line-height: 1.35 !important;
        margin-top: 18px !important;
      }
    }
    @page { margin: 12mm; }
</style>
</head>
<body>
${reportClone.innerHTML}
${autoPrint ? `<script>
  window.addEventListener("load", function () {
    setTimeout(function () { window.focus(); window.print(); }, 500);
  });
</script>` : ""}
</body>
</html>`;
  }, [reportTitle]);

  // ── Print ───────────────────────────────────────────────────────────────────

  const handlePrint = useCallback(() => {
    const html = buildReportHTML(true);
    if (!html) return;
    const printUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const printWindow = window.open(printUrl, "_blank");
    if (!printWindow) {
      URL.revokeObjectURL(printUrl);
      return;
    }
    setTimeout(() => URL.revokeObjectURL(printUrl), 30000);
  }, [buildReportHTML]);

  // ── Export XML ──────────────────────────────────────────────────────────────

  const exportXML = useCallback(() => {
    if (!stats) return;
    const tlpData = TLP_OPTIONS.find(t => t.value === tlp);
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<NexGenReport>
  <metadata>
    <title>${reportTitle}</title><analyst>${analyst}</analyst>
    <tlp classification="${tlp}">${tlpData?.label}</tlp>
    <generated>${formatDate()}</generated><dataset>${fileName}</dataset>
  </metadata>
  <summary>
    <totalEvents>${stats.total}</totalEvents>
    <critical>${stats.critical}</critical><high>${stats.high}</high>
    <medium>${stats.medium}</medium><low>${stats.low}</low>
    <riskScore>${riskScore}</riskScore><riskLabel>${riskLabel.label}</riskLabel>
    <estimatedBreachCost currency="USD">${Math.round(breachCost).toLocaleString()}</estimatedBreachCost>
    <alertFatigueIndex>${alertFatigue}%</alertFatigueIndex>
  </summary>
  <attackDistribution>
    ${stats.attackDist.map(a=>`<attack type="${a.name.replace(/&/g,"&amp;")}" count="${a.value}" percentage="${((a.value/stats.total)*100).toFixed(1)}"/>`).join("\n    ")}
  </attackDistribution>
  <topSourceIPs>
    ${stats.srcIPDist.slice(0,10).map(ip=>`<ip address="${ip.name}" events="${ip.value}"/>`).join("\n    ")}
  </topSourceIPs>
  <topTargetedPorts>
    ${stats.portDist.slice(0,10).map(p=>`<port number="${p.name}" events="${p.value}"/>`).join("\n    ")}
  </topTargetedPorts>
</NexGenReport>`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([xml], { type:"application/xml" }));
    a.download = `nexgen_report_${new Date().toISOString().slice(0,10)}.xml`;
    a.click();
  }, [stats, reportTitle, analyst, tlp, riskScore, riskLabel, breachCost, alertFatigue, fileName]);

  // ── Export CSV ──────────────────────────────────────────────────────────────

  const exportCSV = useCallback(() => {
    if (!data.length) return;
    const csv = [columns.join(","), ...data.slice(0,10000).map(r => columns.map(h=>`"${String(r[h]??"").replace(/"/g,'""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = `nexgen_findings_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  }, [data, columns]);

  const exportHTML = useCallback(() => {
    const html = buildReportHTML();
    if (!html) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([html], { type:"text/html" }));
    a.download = `nexgen_report_${new Date().toISOString().slice(0,10)}.html`;
    a.click();
  }, [buildReportHTML]);

  // ── Shared Props ────────────────────────────────────────────────────────────

  const sharedProps = {
    data, columns, stats, mlResult, fileName,
    riskScore, riskLabel, breachCost, mttd, alertFatigue,
    firewallRules, aiNarrative, aiImpact, aiLoading, aiError,
    tlp, reportTitle, analyst, industry,
    theme, SEVERITY_COLORS, CHART_COLORS: theme.colors.chartColors || CHART_COLORS, TLP_OPTIONS,
  };

  const isReady = data.length > 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <PageLayout title="Incident Reports" subtitle="FORENSIC ANALYSIS · THREAT INTELLIGENCE · COMPLIANCE">
      <style>{PRINT_CSS}</style>

      {/* Control Bar */}
      <div className="no-print" style={{ background:theme.colors.card, border:`1px solid ${theme.colors.cardBorder}`, borderRadius:12, padding:20, marginBottom:20 }}>

        {/* Row 1 — Report config */}
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", alignItems:"flex-end", marginBottom:16 }}>
          {[
            { label:"REPORT TITLE", el:<input value={reportTitle} onChange={e=>setReportTitle(e.target.value)} style={inputStyle} /> },
            { label:"ANALYST",      el:<input value={analyst}     onChange={e=>setAnalyst(e.target.value)}     style={{...inputStyle,width:150}} /> },
            { label:"TLP",          el:(
              <select value={tlp} onChange={e=>setTlp(e.target.value)} style={{...inputStyle,width:"auto",color:TLP_OPTIONS.find(t=>t.value===tlp)?.color}}>
                {TLP_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            )},
            { label:"INDUSTRY",     el:(
              <select value={industry} onChange={e=>setIndustry(e.target.value)} style={{...inputStyle,width:"auto"}}>
                {Object.entries(INDUSTRY_COSTS).map(([k,v]) => <option key={k} value={k}>{v.label} — ${(v.cost/1000000).toFixed(2)}M</option>)}
              </select>
            )},
          ].map(({ label, el }) => (
            <div key={label}>
              <div style={{ fontFamily:"monospace", fontSize:11, color:theme.colors.textMuted, letterSpacing:1, marginBottom:4 }}>{label}</div>
              {el}
            </div>
          ))}
          <label style={{ padding:"9px 18px", background:"rgba(0,212,255,0.1)", border:"1px solid rgba(0,212,255,0.3)", borderRadius:7, color:theme.colors.accent, fontFamily:theme.fonts.display, fontWeight:700, fontSize:11, letterSpacing:1, cursor:"pointer" }}>
            ⬆ LOAD DATASET
            <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />
          </label>
        </div>

        {/* Row 2 — Section toggles */}
        {isReady && (
          <div style={{ borderTop:`1px solid ${theme.colors.cardBorder}`, paddingTop:14 }}>
            <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10, flexWrap:"wrap" }}>
              <span style={{ fontFamily:"monospace", fontSize:11, color:theme.colors.textLabel, letterSpacing:1 }}>SECTIONS:</span>
              <Btn onClick={enableAll}  color={theme.colors.success}>ALL ON</Btn>
              <Btn onClick={disableAll} color={theme.colors.danger}>ALL OFF</Btn>
              {Object.entries(SECTION_GROUPS).map(([gid,g]) => (
                <Btn key={gid} onClick={() => toggleGroup(gid)} color={g.color}>{g.label}</Btn>
              ))}
            </div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {ALL_SECTIONS.map(s => (
                <button key={s.id} onClick={() => toggleSection(s.id)} style={{
                  padding:"4px 10px",
                  background: enabledSections[s.id] ? `${SECTION_GROUPS[s.group].color}22` : theme.colors.rowAltStrong,
                  border: `1px solid ${enabledSections[s.id] ? SECTION_GROUPS[s.group].color : theme.colors.cardBorder}`,
                  borderRadius:4, cursor:"pointer",
                  color: enabledSections[s.id] ? SECTION_GROUPS[s.group].color : theme.colors.textMuted,
                  fontFamily:"monospace", fontSize:10, letterSpacing:1
                }}>{s.label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Row 3 — Export controls */}
        {isReady && (
          <div style={{ display:"flex", gap:8, marginTop:14, flexWrap:"wrap", borderTop:`1px solid ${theme.colors.cardBorder}`, paddingTop:14 }}>
            <Btn onClick={generateNarrative} color="#6CA2AF" disabled={aiLoading}>{aiLoading ? "◌ GENERATING..." : "✦ AI NARRATIVE"}</Btn>
            <Btn onClick={handlePrint}        color="#4F746C">🖨 PRINT / PDF</Btn>
            <Btn onClick={exportHTML}          color="#00BCD4">⬇ EXPORT HTML</Btn>
            <Btn onClick={exportXML}           color="#BF8859">⬇ EXPORT XML</Btn>
            <Btn onClick={exportCSV}           color="#6D7961">⬇ EXPORT CSV</Btn>
          </div>
        )}
      </div>

      {/* Empty State */}
      {!isReady && (
        <div style={{ textAlign:"center", padding:"60px 40px", background:theme.colors.card, border:`1px solid ${theme.colors.cardBorder}`, borderRadius:12 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>📋</div>
          <div style={{ fontFamily:theme.fonts.display, fontSize:18, color:theme.colors.textSecondary, letterSpacing:2 }}>LOAD A DATASET TO GENERATE REPORTS</div>
          <div style={{ fontFamily:theme.fonts.mono, fontSize:12, color:theme.colors.textMuted, marginTop:8 }}>CSV · XLSX · Any vendor format · Auto-detects columns</div>
        </div>
      )}

      {/* Report */}
      {isReady && (
        <div ref={printRef} id="nexgen-report">

          {/* Pre-flight analysis — always shown, not printable */}
          <div className="no-print" data-export-include="preflight">
            <DatasetPreFlight data={data} columns={columns} fileName={fileName} theme={theme} SEVERITY_COLORS={SEVERITY_COLORS} />
          </div>

          <ReportHeader {...sharedProps} onPrint={handlePrint} onXML={exportXML} onCSV={exportCSV} onHTML={exportHTML} />

          {enabledSections.summary         && <ReportSummary         {...sharedProps} onGenerateAI={generateNarrative} />}
          {enabledSections.risk            && <ReportRisk            {...sharedProps} />}
          {enabledSections.timeline        && <ReportTimeline        {...sharedProps} />}
          {enabledSections.attack          && <ReportAttackAnalysis  {...sharedProps} />}
          {enabledSections.network         && <ReportNetworkAnalysis {...sharedProps} />}
          {enabledSections.heatmap         && <ReportHeatmap         {...sharedProps} />}
          {enabledSections.ml              && <ReportMLMetrics       {...sharedProps} />}
          {enabledSections.threat          && <ReportThreatIntel     {...sharedProps} />}
          {enabledSections.compliance      && <ReportCompliance      {...sharedProps} />}
          {enabledSections.recommendations && <ReportRecommendations {...sharedProps} />}
          {enabledSections.breachcost      && <ReportBreachCost      {...sharedProps} />}
          {enabledSections.findings        && <ReportFindings        {...sharedProps} />}

          <div className="report-export-footer" style={{ marginTop:40, paddingTop:20, borderTop:`1px solid ${theme.colors.cardBorder}`, textAlign:"center", fontFamily:"monospace", fontSize:11, color:theme.colors.textMuted }}>
            NexGen Cyber Intelligence Platform · by Hassaan Alrifai · hassaanalrifai.com · contact@hassaanalrifai.com
            <br />Generated: {formatDate()} · TLP:{tlp} · Analyst: {analyst}
          </div>
        </div>
      )}
    </PageLayout>
  );
}

// ── Primitives ────────────────────────────────────────────────────────────────

const inputStyle = {
  background: theme.colors.inputBg, border:`1px solid ${theme.colors.cardBorder}`, borderRadius:6,
  color:theme.colors.textPrimary, fontFamily:"inherit", fontSize:13,
  padding:"8px 12px", outline:"none", width:280
};

const Btn = ({ children, onClick, color, disabled }) => (
  <button onClick={onClick} disabled={disabled} style={{
    padding:"8px 16px", background:`${color}18`, border:`1px solid ${color}44`,
    borderRadius:6, cursor:disabled?"not-allowed":"pointer",
    color, fontFamily:"monospace", fontWeight:700,
    fontSize:11, letterSpacing:1, opacity:disabled?0.5:1, whiteSpace:"nowrap"
  }}>{children}</button>
);
